import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { patientApi } from '../../api/patientApi';

// Async thunks
export const fetchMessageThreads = createAsyncThunk(
  'messaging/fetchThreads',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientApi.getMessageThreads();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch message threads');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messaging/fetchMessages',
  async (threadId, { rejectWithValue }) => {
    try {
      const response = await patientApi.getMessages(threadId);
      return { threadId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch messages');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messaging/sendMessage',
  async (messageData, { rejectWithValue }) => {
    try {
      const response = await patientApi.sendMessage(messageData);
      return { messageData, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to send message');
    }
  }
);

const initialState = {
  threads: {
    data: [],
    loading: false,
    error: null,
    pagination: null
  },
  messages: {
    data: {},
    loading: false,
    error: null
  },
  sending: false,
  sendError: null
};

const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.messages.data = {};
      state.messages.error = null;
    },
    clearSendError: (state) => {
      state.sendError = null;
    },
    addMessageToThread: (state, action) => {
      const { threadId, message } = action.payload;
      if (!state.messages.data[threadId]) {
        state.messages.data[threadId] = [];
      }
      state.messages.data[threadId].push(message);
    },
    markThreadAsRead: (state, action) => {
      const threadId = action.payload;
      const thread = state.threads.data.find(t => t.id === threadId);
      if (thread) {
        thread.unread_count = 0;
        if (thread.last_message) {
          thread.last_message.is_read = true;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch threads
      .addCase(fetchMessageThreads.pending, (state) => {
        state.threads.loading = true;
        state.threads.error = null;
      })
      .addCase(fetchMessageThreads.fulfilled, (state, action) => {
        state.threads.loading = false;
        state.threads.data = action.payload.results;
        state.threads.pagination = action.payload.pagination;
      })
      .addCase(fetchMessageThreads.rejected, (state, action) => {
        state.threads.loading = false;
        state.threads.error = action.payload;
      })
      // Fetch messages
      .addCase(fetchMessages.pending, (state) => {
        state.messages.loading = true;
        state.messages.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messages.loading = false;
        const { threadId, data } = action.payload;
        state.messages.data[threadId] = data.results;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.messages.loading = false;
        state.messages.error = action.payload;
      })
      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.sending = true;
        state.sendError = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sending = false;
        const { messageData, response } = action.payload;
        
        // Add message to the thread
        const threadId = response.thread_id;
        if (!state.messages.data[threadId]) {
          state.messages.data[threadId] = [];
        }
        
        const newMessage = {
          id: response.message_id,
          thread_id: threadId,
          sender: {
            id: messageData.sender_id || '1', // Current user
            name: 'You',
            role: 'patient',
            avatar: null
          },
          recipient: {
            id: messageData.recipient_id,
            name: 'Doctor',
            role: 'doctor',
            avatar: null
          },
          text: messageData.text,
          attachments: messageData.attachments || [],
          is_read: false,
          read_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        state.messages.data[threadId].push(newMessage);
        
        // Update thread's last message
        const thread = state.threads.data.find(t => t.id === threadId);
        if (thread) {
          thread.last_message = {
            id: response.message_id,
            text: messageData.text,
            sender_id: messageData.sender_id || '1',
            created_at: new Date().toISOString(),
            is_read: false
          };
          thread.updated_at = new Date().toISOString();
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sending = false;
        state.sendError = action.payload;
      });
  }
});

export const { clearMessages, clearSendError, addMessageToThread, markThreadAsRead } = messagingSlice.actions;
export default messagingSlice.reducer;
